import { type FormEvent, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, ConfirmModal, Loader, useToast } from '@lms/ui';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { api, listResource } from '@/lib/api';
import { contractTemplateSuccessToast, roleActionErrorToast } from '@/lib/user-feedback';

interface ContractTemplate {
  id: string;
  title: string;
  isActive: boolean;
  contractsCount: number;
  coursesCount: number;
  updatedAt: string;
}

function templateSubtitle(template: ContractTemplate): string {
  if (template.isActive) {
    return 'Plantilla activa para nuevas matrículas.';
  }
  return 'Plantilla inactiva; no se asigna a cursos nuevos.';
}

const PER_PAGE = 10;

export function ContractTemplatesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [modalOpen, setModalOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [page, setPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState<ContractTemplate | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['contracts', 'templates'],
    queryFn: () => listResource<ContractTemplate>('/contracts/templates'),
  });

  const createMutation = useMutation({
    mutationFn: (title: string) =>
      api<{ id: string }>('/contracts/templates', { method: 'POST', body: { title } }),
    onSuccess: (created) => {
      toast(contractTemplateSuccessToast('create'));
      void queryClient.invalidateQueries({ queryKey: ['contracts', 'templates'] });
      setModalOpen(false);
      setNewTitle('');
      navigate(`/contratos/plantillas/${created.id}/editor`);
    },
    onError: (error) => {
      toast(roleActionErrorToast(error, 'No se pudo crear la plantilla'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api(`/contracts/templates/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setDeleteTarget(null);
      toast(contractTemplateSuccessToast('delete'));
      void queryClient.invalidateQueries({ queryKey: ['contracts', 'templates'] });
    },
    onError: (error) => {
      toast(roleActionErrorToast(error, 'No se pudo eliminar la plantilla'));
    },
  });

  function handleCreate(e: FormEvent) {
    e.preventDefault();
    if (!newTitle.trim()) return;
    createMutation.mutate(newTitle.trim());
  }

  const templates = data?.data ?? [];
  const total = templates.length;
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const firstItem = total === 0 ? 0 : (currentPage - 1) * PER_PAGE + 1;
  const lastItem = Math.min(currentPage * PER_PAGE, total);

  const rows = useMemo(() => {
    const start = (currentPage - 1) * PER_PAGE;
    return templates.slice(start, start + PER_PAGE);
  }, [templates, currentPage]);

  return (
    <div className="lms-contract-templates lms-site-admin-hub-page font-sans">
      <div className="mb-6 flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
        <div className="flex items-start gap-4">
          <HubBackLink to="/contratos" label="Volver a Contratos generados" />
          <div className="min-w-0">
            <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Plantillas de contrato</h1>
            <p className="mt-1 max-w-2xl text-dash-on-surface-variant">
              Gestor de plantillas para contratos de servicios al matricular alumnos.
            </p>
          </div>
        </div>
        <button
          type="button"
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-dash-primary px-6 py-3 text-sm font-medium text-white shadow-sm transition-all hover:opacity-90 active:scale-[0.98]"
          onClick={() => setModalOpen(true)}
        >
          <span className="material-symbols-outlined text-[1.25rem]" aria-hidden="true">
            add
          </span>
          Nueva plantilla
        </button>
      </div>

      {error ? <p className="text-sm text-red-600">No se pudieron cargar las plantillas.</p> : null}

      {!error ? (
      <div className="lms-gradebook lms-gradebook--forum">
        <div className="lms-course-forum-card">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader label="Cargando plantillas…" />
            </div>
          ) : total === 0 ? (
            <div className="lms-gradebook__table-wrap">
              <div className="lms-gradebook__empty">
                <div className="lms-gradebook__empty-icon" aria-hidden="true">
                  <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>
                    description
                  </span>
                </div>
                <h3>Sin plantillas</h3>
                <p>Aún no hay plantillas. Crea la primera para asignarla a un curso.</p>
              </div>
            </div>
          ) : (
            <>
              <div className="lms-gradebook__table-wrap">
                <table className="lms-gradebook__table">
                  <thead>
                    <tr>
                      <th className="lms-gradebook__th" scope="col">
                        Plantilla
                      </th>
                      <th className="lms-gradebook__th" scope="col">
                        Estado
                      </th>
                      <th className="lms-gradebook__th" scope="col">
                        Contratos
                      </th>
                      <th className="lms-gradebook__th" scope="col">
                        Cursos
                      </th>
                      <th className="lms-gradebook__th lms-gradebook__th--actions" scope="col">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((template) => {
                      const initial = template.title.trim().charAt(0).toUpperCase() || '?';
                      return (
                        <tr key={template.id}>
                          <th className="lms-gradebook__td" scope="row">
                            <div className="lms-gradebook__student">
                              <div className="lms-gradebook__avatar" aria-hidden="true">
                                {initial}
                              </div>
                              <div className="min-w-0">
                                <Link
                                  to={`/contratos/plantillas/${template.id}/editor`}
                                  className="lms-gradebook__name"
                                >
                                  {template.title}
                                </Link>
                                <div className="lms-gradebook__muted">{templateSubtitle(template)}</div>
                              </div>
                            </div>
                          </th>
                          <td className="lms-gradebook__td">
                            <Badge variant={template.isActive ? 'success' : 'default'}>
                              {template.isActive ? 'Activa' : 'Inactiva'}
                            </Badge>
                          </td>
                          <td className="lms-gradebook__td">{template.contractsCount}</td>
                          <td className="lms-gradebook__td">{template.coursesCount}</td>
                          <td className="lms-gradebook__td lms-gradebook__td--actions">
                            <div className="flex items-center justify-end gap-1">
                              <Link
                                to={`/contratos/plantillas/${template.id}/editor`}
                                className="lms-gradebook__action"
                                title="Editar"
                                aria-label={`Editar ${template.title}`}
                              >
                                <span className="material-symbols-outlined" aria-hidden="true">
                                  edit
                                </span>
                              </Link>
                              <button
                                type="button"
                                className="lms-gradebook__action"
                                title="Eliminar"
                                aria-label={`Eliminar ${template.title}`}
                                onClick={() => setDeleteTarget(template)}
                              >
                                <span className="material-symbols-outlined" aria-hidden="true">
                                  delete
                                </span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="lms-course-forum-card__footer">
                <span className="lms-course-forum-card__meta">
                  Mostrando <strong>{firstItem}</strong> a <strong>{lastItem}</strong> de <strong>{total}</strong>{' '}
                  resultados
                </span>
                <div className="lms-course-forum-card__pager">
                  <button
                    type="button"
                    className="lms-course-forum-card__pager-btn"
                    aria-label="Página anterior"
                    disabled={currentPage <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    <span className="material-symbols-outlined" aria-hidden="true">
                      chevron_left
                    </span>
                  </button>
                  <span className="lms-course-forum-card__pager-current">{currentPage}</span>
                  <button
                    type="button"
                    className="lms-course-forum-card__pager-btn"
                    aria-label="Página siguiente"
                    disabled={currentPage >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    <span className="material-symbols-outlined" aria-hidden="true">
                      chevron_right
                    </span>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      ) : null}

      {modalOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <form
            onSubmit={handleCreate}
            className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
          >
            <h2 className="text-lg font-semibold text-dash-on-surface">Nueva plantilla</h2>
            <label className="mt-4 block text-sm font-medium text-dash-on-surface" htmlFor="template-title">
              Título
            </label>
            <input
              id="template-title"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              className="mt-1 w-full rounded-xl border border-dash-outline-variant px-4 py-3 outline-none transition-all focus:border-dash-secondary focus:ring-1 focus:ring-dash-secondary"
              placeholder="Contrato de servicios educativos"
              required
            />
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-medium text-dash-on-surface-variant transition-colors hover:bg-secondary-100"
                onClick={() => setModalOpen(false)}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="inline-flex items-center justify-center rounded-xl bg-dash-primary px-6 py-2 text-sm font-medium text-white shadow-sm transition-all hover:opacity-90 disabled:opacity-60"
                disabled={createMutation.isPending}
              >
                {createMutation.isPending ? 'Creando…' : 'Crear y editar'}
              </button>
            </div>
          </form>
        </div>
      ) : null}

      <ConfirmModal
        open={Boolean(deleteTarget)}
        title="Eliminar plantilla"
        description={`¿Eliminar "${deleteTarget?.title}"? Solo se puede si no hay contratos generados.`}
        confirmLabel="Eliminar"
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        onClose={() => {
          if (!deleteMutation.isPending) setDeleteTarget(null);
        }}
        loading={deleteMutation.isPending}
      />
    </div>
  );
}
